/**
 * Employee 模块 API 工厂函数
 * 
 * ## 📖 用途
 * 本文件提供员工管理模块的 API 工厂函数，用于生成适配不同请求客户端的 API 方法。
 * 
 * ## ✨ 特点
 * 
 * ### 1. 跨平台共用
 * - ✅ Web 端和 App 端使用同一份代码
 * - ✅ 类型定义完全一致
 * - ✅ 更新一处，多端同步
 * 
 * ### 2. 适配器模式
 * - ✅ 注入请求客户端适配器
 * - ✅ 支持任意 HTTP 库（axios、fetch、uni.request 等）
 * - ✅ 便于测试和 Mock
 * 
 * ### 3. 类型安全
 * - ✅ 完整的 TypeScript 类型支持
 * - ✅ 参数类型自动提示
 * - ✅ 响应类型自动推断
 * 
 * ## 📋 使用方式
 * 
 * ### Web 端使用示例
 * 
 * ```typescript
 * // 1. 创建适配器
 * import { requestClient } from '#/api/request'
 * import { createEmployeeApi } from '@bayes-mes/shared/api/employee/factory'
 * 
 * // 2. 生成 API 实例
 * export const employeeApi = createEmployeeApi({
 *   // GET 请求
 *   get: (url, config) => requestClient.get(url, config),
 *   // POST 请求
 *   post: (url, data, config) => requestClient.post(url, data, config),
 *   // PUT 请求
 *   put: (url, data, config) => requestClient.put(url, data, config),
 *   // DELETE 请求
 *   delete: (url, config) => requestClient.delete(url, config),
 * })
 * 
 * // 3. 使用 API
 * const employees = await employeeApi.getEmployeeList({ page: 1 })
 * const activeEmployees = await employeeApi.getActiveEmployees()
 * ```
 * 
 * ### App 端使用示例
 * 
 * ```typescript
 * // 1. 创建适配器（uni-app）
 * import { createEmployeeApi } from '@bayes-mes/shared/api/employee/factory'
 * 
 * export const employeeApi = createEmployeeApi({
 *   get: (url, config) => uni.request({
 *     url: BASE_URL + url,
 *     method: 'GET',
 *     ...config,
 *   }),
 *   post: (url, data, config) => uni.request({
 *     url: BASE_URL + url,
 *     method: 'POST',
 *     data,
 *     ...config,
 *   }),
 *   // ... put, delete
 * })
 * 
 * // 2. 使用 API（与 Web 端完全一致）
 * const employees = await employeeApi.getEmployeeList({ page: 1 })
 * ```
 * 
 * ## 🔧 适配器接口
 * 
 * 适配器需要实现以下接口：
 * 
 * ```typescript
 * interface ApiAdapter {
 *   get<T>(url: string, config?: any): Promise<T>
 *   post<T>(url: string, data?: any, config?: any): Promise<T>
 *   put<T>(url: string, data?: any, config?: any): Promise<T>
 *   delete<T>(url: string, config?: any): Promise<T>
 * }
 * ```
 * 
 * ## ⚙️ 高级用法
 * 
 * ### 自定义配置
 * 
 * ```typescript
 * export const employeeApi = createEmployeeApi(adapter, {
 *   // 自定义基础路径
 *   basePath: '/api/v2/employees',
 *   // 添加请求拦截器
 *   beforeRequest: (config) => {
 *     // 添加自定义 header
 *     config.headers = { ...config.headers, 'X-Custom': 'value' }
 *     return config
 *   },
 * })
 * ```
 * 
 * ### Mock 测试
 * 
 * ```typescript
 * const mockAdapter = {
 *   get: vi.fn().mockResolvedValue({ data: [] }),
 *   post: vi.fn().mockResolvedValue({ data: { id: 1 } }),
 *   put: vi.fn().mockResolvedValue({ data: { id: 1 } }),
 *   delete: vi.fn().mockResolvedValue({ data: null }),
 * }
 * 
 * const api = createEmployeeApi(mockAdapter)
 * await api.getEmployeeList() // 使用 Mock 数据
 * ```
 * 
 * ## 📚 相关文档
 * 
 * - [API工厂函数生成指南](../../docs/AI协作指南/API工厂函数生成指南.md)
 * - [API使用方式选择指南](../../docs/AI协作指南/API使用方式选择指南.md)
 * - [Employee类型定义](../../types/employee/index.ts)
 * 
 * @module api/employee/factory
 * @version 1.0.0
 * @created 2025-10-18
 * 
 * @对应后端
 * - Controller: backend/app/Modules/Employee/Controllers/EmployeeController.php
 * - Service: backend/app/Modules/Employee/Services/EmployeeService.php
 * - Routes: backend/routes/api.php (prefix: /v1/employees)
 * 
 * @最后更新 2025-10-18
 * @最后验证 2025-10-18
 */

import type {
  Employee,
  EmployeeQueryParams,
  CreateEmployeeForm,
  UpdateEmployeeForm,
  EmployeeStatistics,
  BatchBlacklistEmployeeForm,
  BatchRemoveBlacklistForm,
  BatchResignEmployeeForm,
  BatchRegularizeEmployeeForm,
  BatchDeleteEmployeeForm,
} from '../../types/employee';
import type { ApiResponse, PaginationResult } from '../../types/api';

// ===== 导入 API 路由定义 =====
import { API_ROUTES } from '../routes';

/**
 * HTTP 请求适配器接口
 * 
 * 用于适配不同的 HTTP 请求库（axios、fetch、uni.request 等）
 * 
 * @template T - 响应数据类型
 */
export interface ApiAdapter {
  /**
   * GET 请求
   * 
   * @param url - 请求URL
   * @param config - 请求配置（如 params、headers）
   * @returns Promise<响应数据>
   * 
   * @example
   * ```typescript
   * get: (url, config) => axios.get(url, config)
   * ```
   */
  get<T = any>(url: string, config?: any): Promise<T>;

  /**
   * POST 请求
   * 
   * @param url - 请求URL
   * @param data - 请求体数据
   * @param config - 请求配置
   * @returns Promise<响应数据>
   * 
   * @example
   * ```typescript
   * post: (url, data, config) => axios.post(url, data, config)
   * ```
   */
  post<T = any>(url: string, data?: any, config?: any): Promise<T>;

  /**
   * PUT 请求
   * 
   * @param url - 请求URL
   * @param data - 请求体数据
   * @param config - 请求配置
   * @returns Promise<响应数据>
   * 
   * @example
   * ```typescript
   * put: (url, data, config) => axios.put(url, data, config)
   * ```
   */
  put<T = any>(url: string, data?: any, config?: any): Promise<T>;

  /**
   * DELETE 请求
   * 
   * @param url - 请求URL
   * @param config - 请求配置
   * @returns Promise<响应数据>
   * 
   * @example
   * ```typescript
   * delete: (url, config) => axios.delete(url, config)
   * ```
   */
  delete<T = any>(url: string, config?: any): Promise<T>;
}

// ⚠️ v3.13.0: API_PREFIX 已移除，改用 API_ROUTES.employee.xxx
// 如需引用基础路径，使用: API_ROUTES.employee.list 获取列表端点
// 详见：docs/技术规范/API工厂函数模式选择规范.md

/**
 * Employee API 接口
 * 
 * 定义员工管理模块的所有 API 方法
 */
export interface EmployeeApi {
  /**
   * 获取员工列表（分页）
   * 
   * @param params - 查询参数
   * @returns Promise<分页员工列表>
   * 
   * @example
   * ```typescript
   * const result = await api.getEmployeeList({
   *   keyword: '张三',
   *   employment_status: EmploymentStatus.ACTIVE,
   *   page: 1,
   *   per_page: 15,
   * });
   * console.log(result.data.items); // 员工列表
   * console.log(result.data.total); // 总记录数
   * console.log(result.data.page); // 当前页码
   * ```
   */
  getEmployeeList(
    params?: EmployeeQueryParams
  ): Promise<ApiResponse<PaginationResult<Employee>>>;

  /**
   * 获取所有员工（不分页）
   * 
   * @returns Promise<所有启用的员工列表>
   * 
   * @example
   * ```typescript
   * const result = await api.getAllEmployees();
   * console.log(result.data); // 所有员工
   * ```
   */
  getAllEmployees(): Promise<ApiResponse<Employee[]>>;

  /**
   * 获取所有在职员工（分页）
   * 
   * @param params - 查询参数
   * @returns Promise<分页在职员工列表>
   * 
   * @example
   * ```typescript
   * const result = await api.getActiveEmployees({
   *   keyword: '张三',
   *   department_id: 1,
   *   page: 1,
   *   per_page: 15,
   * });
   * console.log(result.data.items); // 在职员工列表
   * console.log(result.data.pagination); // 分页信息
   * ```
   */
  getActiveEmployees(params?: EmployeeQueryParams): Promise<ApiResponse<PaginationResult<Employee>>>;

  /**
   * 获取员工统计信息
   * 
   * @returns Promise<统计数据（总数、在职数、离职数等）>
   * 
   * @example
   * ```typescript
   * const result = await api.getEmployeeStatistics();
   * console.log(result.total); // 员工总数
   * console.log(result.active); // 在职员工数
   * ```
   */
  getEmployeeStatistics(): Promise<EmployeeStatistics>;

  /**
   * 获取员工详情
   * 
   * @param id - 员工ID
   * @returns Promise<员工详细信息>
   * 
   * @example
   * ```typescript
   * const result = await api.getEmployeeDetail(1);
   * console.log(result.data.name); // 员工姓名
   * console.log(result.data.department); // 所属部门
   * ```
   */
  getEmployeeDetail(id: number | string): Promise<ApiResponse<Employee>>;

  /**
   * 创建员工
   * 
   * @param data - 创建表单数据
   * @returns Promise<新创建的员工信息>
   * 
   * @example
   * ```typescript
   * const result = await api.createEmployee({
   *   employee_no: 'EMP001',
   *   name: '张三',
   *   gender: Gender.MALE,
   *   employment_status: EmploymentStatus.ACTIVE,
   *   status: EmployeeStatus.ENABLED,
   * });
   * console.log(result.data.id); // 新员工ID
   * ```
   */
  createEmployee(data: CreateEmployeeForm): Promise<ApiResponse<Employee>>;

  /**
   * 更新员工
   * 
   * @param id - 员工ID
   * @param data - 更新表单数据
   * @returns Promise<更新后的员工信息>
   * 
   * @example
   * ```typescript
   * const result = await api.updateEmployee(1, {
   *   name: '张三三',
   *   position: '高级工程师',
   * });
   * console.log(result.data.name); // 更新后的姓名
   * ```
   */
  updateEmployee(
    id: number | string,
    data: UpdateEmployeeForm
  ): Promise<ApiResponse<Employee>>;

  /**
   * 删除员工
   * 
   * @param id - 员工ID
   * @returns Promise<删除结果>
   * 
   * @example
   * ```typescript
   * await api.deleteEmployee(1);
   * ```
   * 
   * @throws
   * - 员工已绑定系统账号时无法删除
   */
  deleteEmployee(id: number | string): Promise<ApiResponse<null>>;

  /**
   * 从企业微信同步员工
   * 
   * @returns Promise<同步结果>
   * 
   * @example
   * ```typescript
   * const result = await api.syncEmployeesFromWework();
   * console.log(result.data); // 同步结果
   * ```
   * 
   * @description
   * 功能说明：
   * 1. 从企业微信拉取员工信息
   * 2. 自动创建员工档案
   * 3. 更新已存在的员工信息
   */
  syncEmployeesFromWework(): Promise<ApiResponse<any>>;

  /**
   * 获取黑名单员工列表
   * 
   * @returns Promise<黑名单员工列表>
   * 
   * @example
   * ```typescript
   * const result = await api.getBlacklistEmployees();
   * console.log(result.data); // 黑名单员工
   * ```
   */
  getBlacklistEmployees(): Promise<ApiResponse<Employee[]>>;

  /**
   * 批量拉黑员工
   * 
   * @param data - 批量拉黑表单数据
   * @returns Promise<操作结果>
   * 
   * @example
   * ```typescript
   * const result = await api.batchBlacklistEmployees({
   *   employee_ids: [1, 2, 3],
   *   reason: '违反公司规定'
   * });
   * ```
   */
  batchBlacklistEmployees(data: BatchBlacklistEmployeeForm): Promise<ApiResponse<any>>;

  /**
   * 批量移除黑名单
   * 
   * @param data - 批量移除黑名单表单数据
   * @returns Promise<操作结果>
   * 
   * @example
   * ```typescript
   * const result = await api.batchRemoveBlacklist({
   *   employee_ids: [1, 2, 3],
   *   reason: '表现良好'
   * });
   * ```
   */
  batchRemoveBlacklist(data: BatchRemoveBlacklistForm): Promise<ApiResponse<any>>;

  /**
   * 批量离职
   * 
   * @param data - 批量离职表单数据
   * @returns Promise<操作结果>
   * 
   * @example
   * ```typescript
   * const result = await api.batchResignEmployees({
   *   employee_ids: [1, 2, 3],
   *   leave_date: '2025-01-01',
   *   reason: '个人原因'
   * });
   * ```
   */
  batchResignEmployees(data: BatchResignEmployeeForm): Promise<ApiResponse<any>>;

  /**
   * 批量转正
   * 
   * @param data - 批量转正表单数据
   * @returns Promise<操作结果>
   * 
   * @example
   * ```typescript
   * const result = await api.batchRegularizeEmployees({
   *   employee_ids: [1, 2, 3],
   *   regular_date: '2025-01-01',
   *   regular_file: 'file.pdf'
   * });
   * ```
   */
  batchRegularizeEmployees(data: BatchRegularizeEmployeeForm): Promise<ApiResponse<any>>;

  /**
   * 批量删除员工
   * 
   * @param data - 批量删除表单数据
   * @returns Promise<操作结果>
   * 
   * @example
   * ```typescript
   * const result = await api.batchDeleteEmployees({
   *   employee_ids: [1, 2, 3],
   *   reason: '数据清理'
   * });
   * ```
   * 
   * @throws
   * - 员工已绑定系统账号时无法删除
   */
  batchDeleteEmployees(data: BatchDeleteEmployeeForm): Promise<ApiResponse<any>>;

  /**
   * 拉黑单个员工
   * 
   * @param id - 员工ID
   * @returns Promise<操作结果>
   * 
   * @example
   * ```typescript
   * await api.blacklistEmployee(1);
   * ```
   */
  blacklistEmployee(id: number | string): Promise<ApiResponse<any>>;

  /**
   * 员工离职
   * 
   * @param id - 员工ID
   * @returns Promise<操作结果>
   * 
   * @example
   * ```typescript
   * await api.resignEmployee(1);
   * ```
   */
  resignEmployee(id: number | string, data?: { leave_date?: string; leave_reason?: string }): Promise<ApiResponse<any>>;

  /**
   * 员工转正
   * 
   * @param id - 员工ID
   * @returns Promise<操作结果>
   * 
   * @example
   * ```typescript
   * await api.regularizeEmployee(1);
   * ```
   */
  regularizeEmployee(id: number | string, data?: { regular_date?: string; regular_file?: string }): Promise<ApiResponse<any>>;

  /**
   * 获取可关联的系统账号列表
   * 
   * @returns Promise<未关联员工的用户列表>
   */
  getAvailableUsers(): Promise<ApiResponse<any[]>>;

  /**
   * 关联系统账号
   * 
   * @param employeeId - 员工ID
   * @param userId - 用户ID
   * @returns Promise<操作结果>
   */
  linkUser(employeeId: number | string, userId: number): Promise<ApiResponse<Employee>>;

  /**
   * 解除系统账号关联
   * 
   * @param employeeId - 员工ID
   * @returns Promise<操作结果>
   */
  unlinkUser(employeeId: number | string): Promise<ApiResponse<Employee>>;

  /**
   * 发送账号密码到企业微信
   * 
   * @param employeeId - 员工ID
   * @returns Promise<操作结果>
   * 
   * @description
   * 通过企业微信消息将员工的系统账号和默认密码发送给员工。
   * 前提条件：
   * 1. 员工已关联系统账号
   * 2. 员工已绑定企业微信
   */
  sendCredentials(employeeId: number | string): Promise<ApiResponse<null>>;
}

/**
 * 创建 Employee API 实例
 * 
 * @description
 * 工厂函数，接收 HTTP 请求适配器，返回完整的 Employee API 方法集合。
 * 
 * @param adapter - HTTP 请求适配器
 * @returns Employee API 实例
 * 
 * @example Web 端使用
 * ```typescript
 * import { createEmployeeApi } from '@bayes-mes/shared/api/employee/factory';
 * import { requestClient } from '#/api/request';
 * 
 * export const employeeApi = createEmployeeApi(requestClient);
 * 
 * // 使用 API
 * const employees = await employeeApi.getEmployeeList({ page: 1 });
 * ```
 * 
 * @example App 端使用
 * ```typescript
 * import { createEmployeeApi } from '@bayes-mes/shared/api/employee/factory';
 * import { http } from '@/utils/http';
 * 
 * export const employeeApi = createEmployeeApi(http);
 * ```
 */
export function createEmployeeApi(adapter: ApiAdapter): EmployeeApi {
  return {
    /**
     * 获取员工列表（分页）
     */
    getEmployeeList(params?: EmployeeQueryParams) {
      return adapter.get<ApiResponse<PaginationResult<Employee>>>(
        API_ROUTES.employee.list,
        { params }
      );
    },

    /**
     * 获取所有员工（不分页）
     */
    getAllEmployees() {
      return adapter.get<ApiResponse<Employee[]>>(API_ROUTES.employee.all);
    },

    /**
     * 获取所有在职员工（分页）
     */
    getActiveEmployees(params?: EmployeeQueryParams) {
      return adapter.get<ApiResponse<PaginationResult<Employee>>>(
        API_ROUTES.employee.active,
        { params }
      );
    },

    /**
     * 获取员工统计信息
     */
    getEmployeeStatistics() {
      return adapter.get<EmployeeStatistics>(API_ROUTES.employee.statistics);
    },

    /**
     * 获取员工详情
     */
    getEmployeeDetail(id: number | string) {
      return adapter.get<ApiResponse<Employee>>(API_ROUTES.employee.detail(id));
    },

    /**
     * 创建员工
     */
    createEmployee(data: CreateEmployeeForm) {
      return adapter.post<ApiResponse<Employee>>(API_ROUTES.employee.create, data);
    },

    /**
     * 更新员工
     */
    updateEmployee(id: number | string, data: UpdateEmployeeForm) {
      return adapter.put<ApiResponse<Employee>>(API_ROUTES.employee.employees(id), data);
    },

    /**
     * 删除员工
     */
    deleteEmployee(id: number | string) {
      return adapter.delete<ApiResponse<null>>(API_ROUTES.employee.delete(id));
    },

    /**
     * 从企业微信同步员工
     */
    syncEmployeesFromWework() {
      return adapter.post<ApiResponse<any>>(API_ROUTES.employee.syncWework);
    },

    /**
     * 获取黑名单员工列表
     */
    getBlacklistEmployees() {
      return adapter.get<ApiResponse<Employee[]>>(API_ROUTES.employee.blacklist);
    },

    /**
     * 批量拉黑员工
     */
    batchBlacklistEmployees(data: BatchBlacklistEmployeeForm) {
      return adapter.post<ApiResponse<any>>(API_ROUTES.employee.batchAdd, data);
    },

    /**
     * 批量移除黑名单
     */
    batchRemoveBlacklist(data: BatchRemoveBlacklistForm) {
      return adapter.post<ApiResponse<any>>(API_ROUTES.employee.batchRemove, data);
    },

    /**
     * 批量离职
     */
    batchResignEmployees(data: BatchResignEmployeeForm) {
      return adapter.post<ApiResponse<any>>(API_ROUTES.employee.batchResign, data);
    },

    /**
     * 批量转正
     */
    batchRegularizeEmployees(data: BatchRegularizeEmployeeForm) {
      return adapter.post<ApiResponse<any>>(API_ROUTES.employee.batchRegularize, data);
    },

    /**
     * 批量删除员工
     */
    batchDeleteEmployees(data: BatchDeleteEmployeeForm) {
      return adapter.post<ApiResponse<any>>(API_ROUTES.employee.batchDelete, data);
    },

    /**
     * 拉黑单个员工
     */
    blacklistEmployee(id: number | string) {
      return adapter.post<ApiResponse<any>>(API_ROUTES.employee.employeesBlacklist(id));
    },

    /**
     * 员工离职
     */
    resignEmployee(id: number | string, data?: { leave_date?: string; leave_reason?: string }) {
      return adapter.post<ApiResponse<any>>(API_ROUTES.employee.resign(id), data);
    },

    /**
     * 员工转正
     */
    regularizeEmployee(id: number | string, data?: { regular_date?: string; regular_file?: string }) {
      return adapter.post<ApiResponse<any>>(API_ROUTES.employee.regularize(id), data);
    },

    /**
     * 获取可关联的系统账号列表
     */
    getAvailableUsers() {
      return adapter.get<ApiResponse<any[]>>(API_ROUTES.employee.availableUsers);
    },

    /**
     * 关联系统账号
     */
    linkUser(employeeId: number | string, userId: number) {
      return adapter.post<ApiResponse<Employee>>(API_ROUTES.employee.linkUser(employeeId), { user_id: userId });
    },

    /**
     * 解除系统账号关联
     */
    unlinkUser(employeeId: number | string) {
      return adapter.post<ApiResponse<Employee>>(API_ROUTES.employee.unlinkUser(employeeId));
    },

    /**
     * 发送账号密码到企业微信
     */
    sendCredentials(employeeId: number | string) {
      return adapter.post<ApiResponse<null>>(API_ROUTES.employee.sendCredentials(employeeId));
    },
  };
}

/**
 * 默认导出工厂函数
 */
export default createEmployeeApi;
